import React, { useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAppSelector } from "hooks";
import { personalasTabsFor } from "features/personnel/personnel-helpers";
import useScrollActiveTabIntoView from "features/personnel/useScrollActiveTabIntoView";

/**
 * «Darbuotojai | Darbo sutartys | Pareigos | Skyriai» strip over the Personalas screens
 * (one scrolling line on a phone, where the four tabs do not fit side by side).
 * Each tab shows only for the rights of its route (personalasTabsFor): a
 * department head with the derived subordinates permission sees
 * «Darbuotojai» alone.
 */
export default function PersonalasTabs({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const role = useAppSelector((state) => state.authReducer.role);
  const tabs = personalasTabsFor(role);
  const { pathname } = useLocation();
  const strip = useRef<HTMLElement>(null);
  useScrollActiveTabIntoView(strip, pathname);
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      // md:-mb-px only from md up: inside the scrolling strip the overhang would be clipped.
      "shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium max-md:py-3 md:-mb-px",
      isActive
        ? "border-blue-600 text-blue-700"
        : "border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900"
    );
  return (
    <nav ref={strip} className="mb-4 flex gap-2 overflow-x-auto border-b border-gray-200 md:overflow-visible" aria-label={t("hr_module")}>
      {tabs.employees && (
        <NavLink to={`/app/${companyId}/personnel`} className={tabClass}>
          {t("personnel_tab_employees")}
        </NavLink>
      )}
      {tabs.onboardings && (
        <NavLink to={`/app/${companyId}/onboarding`} end className={tabClass}>
          {t("positions_tab_onboardings")}
        </NavLink>
      )}
      {tabs.positions && (
        <NavLink to={`/app/${companyId}/positions`} className={tabClass}>
          {t("positions_title")}
        </NavLink>
      )}
      {tabs.departments && (
        <NavLink to={`/app/${companyId}/departments`} className={tabClass}>
          {t("departments")}
        </NavLink>
      )}
    </nav>
  );
}
